import { useMemo } from 'react';
import {
  Area,
  AreaChart,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

import {
  DIRECTION_LABEL,
  DIRECTION_TONE,
  formatPeriodLong,
  isFullyBackfilled,
  measuredCount,
  trendSeries,
} from './review';

import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/cn';
import type { ReadinessTrend } from '@/lib/api/review';

export interface ReadinessTrendChartProps {
  trend?: ReadinessTrend | null;
  loading?: boolean;
  /**
   * Score below which a student is in the bottom tier, from the published
   * formula. Omitted means no line — better than one drawn at a guessed value
   * that the tier badge next to it would contradict.
   */
  atRiskThreshold?: number;
  className?: string;
}

/**
 * A student's readiness over the review periods — "improving, flat or
 * slipping" in the college's own words.
 *
 * The chart deliberately refuses to interpolate across unmeasured weeks
 * (`connectNulls` is off). A gap in the line is honest; a straight line drawn
 * through a month with no data invents a measurement, and inventing
 * measurements is the exact failure that made the old score indefensible.
 */
export function ReadinessTrendChart({
  trend,
  loading = false,
  atRiskThreshold,
  className,
}: ReadinessTrendChartProps) {
  const series = useMemo(() => trendSeries(trend?.points ?? []), [trend?.points]);
  const measured = measuredCount(trend ?? undefined);

  if (loading) {
    return (
      <div className={cn('space-y-2', className)}>
        <Skeleton className="h-4 w-32" />
        <Skeleton className="h-[120px] w-full" />
      </div>
    );
  }

  const direction = trend?.direction ?? 'unknown';

  return (
    <div className={className}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <h4 className="text-[13px] font-semibold text-foreground">Readiness trend</h4>
          <span
            className={cn(
              'rounded-full border px-1.5 py-px text-[10.5px] font-semibold',
              DIRECTION_TONE[direction],
            )}
          >
            {DIRECTION_LABEL[direction]}
          </span>
        </div>
        {trend?.change !== undefined && (
          <span
            className={cn(
              'text-[11.5px] font-semibold tabular-nums',
              trend.change > 0
                ? 'text-emerald-700'
                : trend.change < 0
                  ? 'text-rose-700'
                  : 'text-muted-foreground',
            )}
          >
            {trend.change > 0 ? `+${trend.change}` : trend.change} points over{' '}
            {measured} {measured === 1 ? 'reading' : 'readings'}
          </span>
        )}
      </div>

      {/*
        One measured point is not a trend. Saying so is better than drawing a
        single dot that implies a line, or an empty frame that implies nothing
        happened.
      */}
      {measured < 2 ? (
        <p className="mt-3 rounded-md border border-dashed border-border bg-muted/40 px-3 py-4 text-[11.5px] leading-relaxed text-muted-foreground">
          {measured === 0
            ? 'No readiness has been measured for this student yet. A reading is taken every week.'
            : 'Only one reading so far. A second one next week makes a direction meaningful.'}
        </p>
      ) : (
        <div className="mt-2 h-[128px] w-full">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={series} margin={{ top: 6, right: 8, bottom: 0, left: 0 }}>
              <defs>
                <linearGradient id="readinessTrendFill" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="hsl(var(--primary))" stopOpacity={0.22} />
                  <stop offset="100%" stopColor="hsl(var(--primary))" stopOpacity={0.02} />
                </linearGradient>
              </defs>
              {atRiskThreshold !== undefined && (
                <ReferenceLine
                  y={atRiskThreshold}
                  stroke="currentColor"
                  strokeDasharray="3 3"
                  className="text-rose-300"
                  label={{
                    value: 'at risk',
                    position: 'insideBottomRight',
                    fontSize: 9,
                    fill: 'currentColor',
                    className: 'text-rose-400',
                  }}
                />
              )}
              <XAxis
                dataKey="label"
                tick={{ fontSize: 10 }}
                tickLine={false}
                axisLine={false}
                interval="preserveStartEnd"
                minTickGap={18}
              />
              <YAxis
                domain={[0, 100]}
                ticks={[0, 50, 100]}
                tick={{ fontSize: 10 }}
                tickLine={false}
                axisLine={false}
                width={30}
              />
              <Tooltip content={<TrendTooltip />} />
              <Area
                type="monotone"
                dataKey="score"
                stroke="hsl(var(--primary))"
                strokeWidth={2}
                fill="url(#readinessTrendFill)"
                // The whole point: unmeasured weeks stay as gaps.
                connectNulls={false}
                dot={{ r: 2 }}
                activeDot={{ r: 4 }}
                // No entry animation. This is a data readout, not a reveal:
                // recharts replays the growth from the baseline on every
                // re-render, so a background refetch would make a settled
                // trend line visibly redraw itself for no reason.
                isAnimationActive={false}
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      )}

      <TrendCaveats trend={trend ?? undefined} />
    </div>
  );
}

interface TooltipPayload {
  payload?: { label: string; score: number | null; tier?: string; backfilled: boolean; coverage: number };
}

function TrendTooltip({
  active,
  payload,
}: {
  active?: boolean;
  payload?: TooltipPayload[];
}) {
  const point = active ? payload?.[0]?.payload : undefined;
  if (!point || point.score === null) return null;
  return (
    <div className="rounded-md border border-border bg-popover px-2 py-1.5 text-[11px] shadow-sm">
      <p className="font-semibold text-foreground">
        {point.score}
        {point.tier ? <span className="ml-1 font-normal text-muted-foreground">tier {point.tier}</span> : null}
      </p>
      <p className="text-muted-foreground">Week of {point.label}</p>
      <p className="text-muted-foreground">{Math.round(point.coverage * 100)}% of the formula measured</p>
      {point.backfilled && <p className="text-muted-foreground">Reconstructed reading</p>}
    </div>
  );
}

/**
 * The two things about this chart a reader could otherwise be misled by.
 * Stated under the chart rather than buried in a tooltip, because both change
 * how the slope should be read.
 */
function TrendCaveats({ trend }: { trend?: ReadinessTrend }) {
  const points = trend?.points ?? [];
  const measured = points.filter((p) => p.has_data);
  if (measured.length === 0) return null;

  const notes: string[] = [];
  if (trend?.mixed_formula_versions) {
    notes.push(
      'This range spans a change to the scoring formula, so part of the movement is the formula, not the student.',
    );
  }
  if (isFullyBackfilled(trend)) {
    notes.push(
      'These readings were reconstructed from dated submissions, attendance and marks rather than captured each week, so they are measured against the current curriculum size.',
    );
  }

  const first = measured[0];
  const last = measured[measured.length - 1];

  return (
    <div className="mt-2 space-y-1">
      <p className="text-[10.5px] leading-tight text-muted-foreground">
        {formatPeriodLong(first.period_start)} — {formatPeriodLong(last.period_start)}
      </p>
      {notes.map((note) => (
        <p key={note} className="text-[10.5px] leading-snug text-amber-700">
          {note}
        </p>
      ))}
    </div>
  );
}
